/**
 * Utility functions for grouping and formatting Safety Observation reports.
 */

/**
 * Format date for report display (DD/MM/YYYY).
 * Prefers ISO date string (YYYY-MM-DD) from backend report_date.
 */
export function formatReportDisplayDate(reportDate, createdAt) {
  if (reportDate) {
    const parts = String(reportDate).trim().split("-");
    if (parts.length === 3) {
      const [y, m, d] = parts;
      return `${d}/${m}/${y}`;
    }
    return reportDate;
  }
  if (createdAt) {
    try {
      const d = new Date(createdAt);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
    } catch {
      // ignore error
    }
  }
  return "";
}

/**
 * Groups Safety Observations into canonical report batches regardless of status.
 *
 * Rules:
 * 1. Observations belonging to the same report batch / report number / day are grouped together.
 * 2. Status (open, pending rectification, pending review, approved, closed) does not exclude observations.
 * 3. Grouping key prefers report_batch_id (canonical identity).
 * 4. Fallback to report_number if report_batch_id is absent.
 * 5. Distinct report_batch_ids are NEVER merged, even if display report numbers match.
 * 6. Returns sorted array with newest reports first.
 */
export function groupObservationsByReportBatch(items = [], options = {}) {
  const { onlyApproved = false } = options;
  if (!Array.isArray(items)) {
    return [];
  }

  const groups = new Map();

  items
    .filter((item) => {
      if (!onlyApproved) return true;
      return String(item?.status || "").trim().toLowerCase() === "approved";
    })
    .forEach((item) => {
      const batchId = item?.report_batch_id ?? null;
      const reportNumber = String(item?.report_number || "").trim();

      if (!batchId && !reportNumber) {
        return;
      }

      // Canonical identity is report_batch_id when available
      const key = batchId ? `batch:${batchId}` : `report:${reportNumber}`;

      const reportDate =
        item?.report_date ||
        (item?.created_at ? String(item.created_at).slice(0, 10) : "");

      const displayDate = formatReportDisplayDate(
        item?.report_date,
        item?.created_at,
      );

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          reportBatchId: batchId,
          reportNumber,
          reportDate,
          displayDate,
          date: displayDate || reportDate,
          reportName: reportNumber
            ? `Observation Report - ${reportNumber}`
            : `Observation Report - ${displayDate || "Report"}`,
          items: [],
          firstItemId: item.id,
        });
      }

      const grp = groups.get(key);
      if (!grp.reportBatchId && batchId) {
        grp.reportBatchId = batchId;
      }
      if (!grp.reportNumber && reportNumber) {
        grp.reportNumber = reportNumber;
        grp.reportName = `Observation Report - ${reportNumber}`;
      }
      grp.items.push(item);
    });

  return Array.from(groups.values()).sort((a, b) => {
    const aDate = new Date(a.reportDate || 0);
    const bDate = new Date(b.reportDate || 0);
    return bDate - aDate;
  });
}

/**
 * Groups approved Safety Observations into canonical report batches.
 * Preserved for backward compatibility.
 */
export function groupApprovedObservationsByReportBatch(items = []) {
  return groupObservationsByReportBatch(items, { onlyApproved: true });
}
